/**
 * The broadcast, assembled into what an editor has to decide about it.
 *
 * The overview used to answer "how many of each thing came out" and stop there, which is a report
 * about the pipeline rather than about the journalism. An editor arriving after a mañanera has one
 * question — *what needs me* — and the answer is already in the payloads: a contradiction the
 * verification found, a draft the gates stopped, an accusation nobody has cleared, a story that
 * produced no version at all.
 *
 * Pure and fetch-free, like the claim thread: it takes what the workspaces already loaded and
 * joins it on `story_id`, which is why it can be tested and why no endpoint was needed for it.
 */

import { observedSocialFloor } from 'src/lib/storyGate';
import type {
  EditorialOutput,
  EditorialPrenota,
  EditorialStreamSummary,
  EditorialVerification,
  EditorialXReview,
} from 'src/types/editorial';

export type StoryDrafts = {
  total: number;
  ready: number;
  blocked: number;
  failed: number;
  /** Words in the longest version — what the story can actually carry, not an average of tries. */
  words: number;
};

export type StoryRow = {
  storyId: string;
  headline: string;
  /** `allowed` stories reached research; the rest were stopped at normalisation. */
  eligibility: string;
  /** Null when X review never ran or was skipped — null is a state, zero is a score. */
  xScore: number | null;
  xStatus: 'scored' | 'skipped' | 'failed' | 'absent';
  verification: string | null;
  conflicts: number;
  /** External facts this story leans on that a person has to clear before publication. */
  riskyFacts: number;
  drafts: StoryDrafts;
  gateCodes: string[];
  needsHuman: boolean;
  /**
   * Whether the drafting gate deliberately excluded this story.
   *
   * A gate that fired is a fact about the pipeline's configuration, not a job for an editor. Nine
   * of twelve entries on the first version of this screen were "eligible story with no draft" for
   * stories the X floor had refused on purpose, which is the fastest way to teach someone to stop
   * reading the list.
   */
  gatedOut: boolean;
  /** Why, in words, when there is a reason. Absence is a state, not a blank. */
  absenceReason: string;
};

export type BoardFigures = {
  stories: number;
  storiesWithDraft: number;
  draftsReady: number;
  draftsBlocked: number;
  verified: number;
  contradictions: number;
  needsHuman: number;
};

export const DECISION_KINDS = [
  'contradiction',
  'draft_blocked',
  'human_review',
  'verification_blocked',
  'research_failed',
  'no_draft',
] as const;
export type DecisionKind = (typeof DECISION_KINDS)[number];

export type Decision = {
  kind: DecisionKind;
  storyId: string;
  headline: string;
  detail: string;
  /** Where the editor goes to act on it. Relative to the stream. */
  to: string;
};

/** Most consequential first. A contradiction is a story; a missing draft is an errand. */
const DECISION_RANK: Record<DecisionKind, number> = {
  contradiction: 0,
  draft_blocked: 1,
  human_review: 2,
  verification_blocked: 3,
  research_failed: 4,
  no_draft: 5,
};

const READY = 'pending_human_review';
const BLOCKED = 'validation_failed';

export type BoardInput = {
  prenotas: EditorialPrenota[];
  xReviews: EditorialXReview[];
  verifications: EditorialVerification[];
  outputs: EditorialOutput[];
};

export function buildStoryRows({ prenotas, xReviews, verifications, outputs }: BoardInput): StoryRow[] {
  const reviewByStory = indexXReviews(xReviews);
  const verificationByStory = new Map(verifications.map(item => [item.storyId, item]));
  const outputsByStory = groupBy(outputs, output => output.storyId);
  // The server never tells the browser its EDITORIAL_MIN_X_SCORE. The lowest score that actually
  // produced a draft in this broadcast is an upper bound on it, and the only honest one available.
  //
  // The index comes from `reviewByStory` rather than from `storyGate`'s own builder: that one keys
  // off `payload.story_id` and this one off the row's `storyId`, and two indexes that disagree
  // about which field identifies a review would put a story on the wrong side of the floor.
  const floor = observedSocialFloor(outputs, scoreIndexFrom(reviewByStory));

  const rows = prenotas.map(prenota => {
    const storyId = prenota.storyId;
    const review = reviewByStory.get(storyId);
    const verification = verificationByStory.get(storyId);
    const storyOutputs = outputsByStory.get(storyId) ?? [];
    const facts = verification?.payload.external_facts ?? [];

    const xScore = review?.payload.overall_score ?? null;
    const eligibility = prenota.payload.eligibility?.decision ?? 'unknown';
    const gatedOut = isGatedOut({ eligibility, xScore, floor });

    return {
      storyId,
      headline: prenota.payload.headline || storyId,
      eligibility,
      xScore,
      xStatus: xStatusOf(review),
      verification: verification?.payload.status ?? null,
      conflicts: (verification?.payload.conflicts ?? []).length,
      riskyFacts: facts.filter(fact => fact.human_review_required).length,
      drafts: summariseDrafts(storyOutputs),
      gateCodes: collectGateCodes(storyOutputs),
      needsHuman: storyOutputs.some(output => output.payload.validation?.risk?.human_review_required),
      gatedOut,
      absenceReason: absenceReasonFor({ eligibility, xScore, floor, gatedOut }),
    } satisfies StoryRow;
  });

  // Stories that need something first, then by X score, then by id so two loads agree.
  return rows.sort(
    (a, b) =>
      Number(needsAttention(b)) - Number(needsAttention(a)) ||
      (b.xScore ?? -1) - (a.xScore ?? -1) ||
      a.storyId.localeCompare(b.storyId)
  );
}

function needsAttention(row: StoryRow): boolean {
  return (
    row.conflicts > 0 ||
    row.drafts.blocked > 0 ||
    row.needsHuman ||
    row.verification === 'contradiction_lead' ||
    (row.eligibility === 'allowed' && row.drafts.total === 0 && !row.gatedOut)
  );
}

/** A story the pipeline was configured to skip. Not a defect, and not a decision. */
function isGatedOut({
  eligibility,
  xScore,
  floor,
}: {
  eligibility: string;
  xScore: number | null;
  floor: number | null;
}): boolean {
  if (eligibility !== 'allowed') return true;
  // No score at all is gated out by design: the pipeline refuses to draft an unknown score.
  if (xScore === null) return true;
  return floor !== null && xScore < floor;
}

function absenceReasonFor({
  eligibility,
  xScore,
  floor,
  gatedOut,
}: {
  eligibility: string;
  xScore: number | null;
  floor: number | null;
  gatedOut: boolean;
}): string {
  if (!gatedOut) return '';
  if (eligibility !== 'allowed') return 'la normalización la bloqueó';
  if (xScore === null) return 'sin score de X, y sin score no se redacta';
  if (floor !== null) return `X ${Math.round(xScore)}, por debajo del ${floor} más bajo que sí produjo nota`;
  return 'quedó por debajo del piso de relevancia social';
}

export function buildFigures(rows: StoryRow[]): BoardFigures {
  return {
    stories: rows.length,
    storiesWithDraft: rows.filter(row => row.drafts.total > 0).length,
    draftsReady: sum(rows, row => row.drafts.ready),
    draftsBlocked: sum(rows, row => row.drafts.blocked),
    verified: rows.filter(row => row.verification === 'verified').length,
    contradictions: rows.filter(row => row.verification === 'contradiction_lead' || row.conflicts > 0).length,
    needsHuman: rows.filter(row => row.needsHuman || row.riskyFacts > 0).length,
  };
}

/**
 * What an editor has to look at, in the order they should look at it.
 *
 * One entry per story per reason, not one per story: a story whose verification found a
 * contradiction *and* whose draft was stopped by a gate is two different jobs.
 */
export function buildDecisions(rows: StoryRow[]): Decision[] {
  const decisions: Decision[] = [];

  for (const row of rows) {
    const base = { storyId: row.storyId, headline: row.headline };

    if (row.verification === 'contradiction_lead' || row.conflicts > 0) {
      decisions.push({
        ...base,
        kind: 'contradiction',
        detail:
          row.conflicts > 0
            ? `${row.conflicts} contradicción${row.conflicts === 1 ? '' : 'es'} material${row.conflicts === 1 ? '' : 'es'} documentada${row.conflicts === 1 ? '' : 's'}. La disputa es la historia.`
            : 'Un claim crítico quedó contradicho. La disputa es la historia.',
        to: 'verification',
      });
    }

    if (row.drafts.blocked > 0) {
      const codes = row.gateCodes.length > 0 ? ` (${row.gateCodes.join(', ')})` : '';
      decisions.push({
        ...base,
        kind: 'draft_blocked',
        detail: `${row.drafts.blocked} de ${row.drafts.total} versiones no pasaron validación${codes}.`,
        to: 'notes',
      });
    }

    if (row.needsHuman || row.riskyFacts > 0) {
      decisions.push({
        ...base,
        kind: 'human_review',
        detail:
          row.riskyFacts > 0
            ? `${row.riskyFacts} hecho${row.riskyFacts === 1 ? '' : 's'} de riesgo alto en el respaldo: nadie puede publicarla sin leerla.`
            : 'La redacción quedó marcada para revisión humana obligatoria.',
        to: 'notes',
      });
    }

    if (row.verification === 'blocked_no_sources' || row.verification === 'blocked_critical_claim') {
      decisions.push({
        ...base,
        kind: 'verification_blocked',
        detail:
          row.verification === 'blocked_no_sources'
            ? 'Ninguna fuente aceptada sostiene el núcleo. Formato atribuido, o se cae.'
            : 'Un claim crítico no se pudo resolver ni atribuir con seguridad. Hay que repensar el ángulo.',
        to: 'verification',
      });
    }

    // Distinct from `blocked_no_sources` on purpose: that one claims no sources exist, this one
    // admits we never looked. Telling an editor a story is a dead end when it was never attempted
    // is the single most expensive confusion this screen can create.
    if (row.verification === 'research_failed') {
      decisions.push({
        ...base,
        kind: 'research_failed',
        detail: 'La investigación no llegó a correr — falla técnica o presupuesto agotado. Se puede reintentar.',
        to: 'verification',
      });
    }

    // Only when nothing explains it. A story the X floor refused has a reason, and that reason
    // belongs beside the story in the roster rather than in the list of things a person must do.
    if (row.eligibility === 'allowed' && row.drafts.total === 0 && !row.gatedOut) {
      decisions.push({
        ...base,
        kind: 'no_draft',
        detail: 'Pasó los filtros y aun así no tiene ninguna versión redactada.',
        to: 'stories',
      });
    }
  }

  return decisions.sort(
    (a, b) => DECISION_RANK[a.kind] - DECISION_RANK[b.kind] || a.storyId.localeCompare(b.storyId)
  );
}

/** Milliseconds by stage across every draft — where the time in a broadcast actually goes. */
export function buildStageTime(outputs: EditorialOutput[]): Array<{ stage: string; ms: number }> {
  const totals = new Map<string, number>();
  for (const output of outputs) {
    for (const [stage, ms] of Object.entries(output.payload.generation?.stage_ms ?? {})) {
      totals.set(stage, (totals.get(stage) ?? 0) + ms);
    }
  }
  return [...totals.entries()]
    .map(([stage, ms]) => ({ stage, ms }))
    .sort((a, b) => b.ms - a.ms || a.stage.localeCompare(b.stage));
}

// ── Pipeline stages ─────────────────────────────────────────────────────────

export type RailStanding = 'done' | 'partial' | 'skipped' | 'not_reached' | 'not_built';

export type RailStage = {
  step: number;
  name: string;
  standing: RailStanding;
  /** Real, or the stage is marked absent. Never a placeholder zero. */
  detail: string;
  /** Relative to the stream. Stages with no screen render inert instead. */
  to?: string;
};

/** The stream rail starts after capture, which is the origin of the broadcast, not a stage to revisit. */
const RAIL_STAGE_NAMES = [
  'Guion',
  'Claims',
  'Prenotas',
  'Relevancia',
  'Verificación',
  'Notas y revisión',
  'Publicación',
  'Seguimiento',
] as const;

/**
 * The pipeline destinations after capture, with their real standing, for the stream shell's rail.
 *
 * One builder for the whole app so the rail and any other surface never disagree about what
 * "Verificación" means on this broadcast. `counts` null means the summary is still in flight:
 * the stages render with a neutral standing and a dot, never with invented zeros.
 */
export function buildRailStages(counts: EditorialStreamSummary | null): RailStage[] {
  if (!counts) {
    return RAIL_STAGE_NAMES.map((name, index) => ({ step: index + 1, name, standing: 'not_reached', detail: '·' }));
  }

  return [
    {
      step: 1,
      name: 'Guion',
      standing: counts.turns > 0 ? 'done' : 'not_reached',
      detail: counts.turns > 0 ? `${counts.turns} turnos` : 'sin guion',
      to: 'transcript',
    },
    {
      step: 2,
      name: 'Claims',
      standing: counts.claims > 0 ? 'done' : 'not_reached',
      detail: `${counts.claims} normalizados`,
      to: 'claims',
    },
    {
      step: 3,
      name: 'Prenotas',
      standing: counts.prenotas > 0 ? 'done' : 'not_reached',
      detail: `${counts.prenotas} historias`,
      to: 'stories',
    },
    {
      step: 4,
      name: 'Relevancia',
      standing: counts.criteriaMatches > 0 || counts.xReviews > 0 ? 'done' : 'not_reached',
      detail: `${counts.criteriaMatches} coincidencias · ${counts.xReviews} en X`,
      to: 'relevance',
    },
    {
      step: 5,
      name: 'Verificación',
      // Partial is the honest reading when only some stories cleared the X-score gate.
      standing:
        counts.verifications === 0 ? 'skipped' : counts.verifications < counts.prenotas ? 'partial' : 'done',
      detail: counts.verifications === 0 ? 'no se ejecutó' : `${counts.verifications} de ${counts.prenotas} historias`,
      to: 'verification',
    },
    {
      step: 6,
      name: 'Notas y revisión',
      standing: counts.outputs > 0 ? 'done' : 'not_reached',
      detail: `${counts.outputs} borradores · ${counts.reviews} revisiones`,
      to: 'notes',
    },
    { step: 7, name: 'Publicación', standing: 'not_built', detail: 'etapa no construida' },
    { step: 8, name: 'Seguimiento', standing: 'not_built', detail: 'etapa no construida' },
  ];
}

// ── helpers ──────────────────────────────────────────────────────────────────

/**
 * story_id → its X review, including the ids a clustered review covers.
 *
 * A review of a cluster carries `source_story_ids`, and the stories it absorbed have no review of
 * their own. Reading only `story_id` reported them as never reviewed.
 */
function indexXReviews(xReviews: EditorialXReview[]): Map<string, EditorialXReview> {
  const index = new Map<string, EditorialXReview>();
  for (const review of xReviews) {
    for (const id of [...(review.payload.source_story_ids ?? []), review.storyId]) {
      if (id) index.set(id, review);
    }
  }
  return index;
}

function scoreIndexFrom(reviews: Map<string, EditorialXReview>): Map<string, number> {
  const scores = new Map<string, number>();
  for (const [storyId, review] of reviews) {
    const score = review.payload.overall_score;
    if (score !== undefined) scores.set(storyId, score);
  }
  return scores;
}

function xStatusOf(review: EditorialXReview | undefined): StoryRow['xStatus'] {
  if (!review) return 'absent';
  // Design System §7: a scored review has `overall_score` and no status field, so the presence of
  // the status is the correct check rather than the absence of the score.
  if (review.payload.social_review_status === 'skipped') return 'skipped';
  if (review.payload.social_review_status === 'failed') return 'failed';
  return review.payload.overall_score === undefined ? 'absent' : 'scored';
}

function summariseDrafts(outputs: EditorialOutput[]): StoryDrafts {
  const words = outputs.map(output => output.payload.validation?.length?.word_count ?? countWords(output));
  return {
    total: outputs.length,
    ready: outputs.filter(output => output.payload.status === READY).length,
    blocked: outputs.filter(output => output.payload.status === BLOCKED).length,
    failed: outputs.filter(output => output.payload.status !== READY && output.payload.status !== BLOCKED).length,
    words: words.length > 0 ? Math.max(...words) : 0,
  };
}

/** Older drafts carry no measured length, and a zero there would read as an empty piece. */
function countWords(output: EditorialOutput): number {
  return (output.payload.content?.body_blocks ?? []).reduce(
    (total, block) => total + String(block.text ?? '').trim().split(/\s+/).filter(Boolean).length,
    0
  );
}

function collectGateCodes(outputs: EditorialOutput[]): string[] {
  const codes = new Set<string>();
  for (const output of outputs) {
    for (const code of Object.keys(output.payload.validation?.evidence_discipline?.codes ?? {})) {
      codes.add(code);
    }
  }
  return [...codes].sort();
}

function groupBy<T>(items: T[], key: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const bucket = groups.get(key(item));
    if (bucket) bucket.push(item);
    else groups.set(key(item), [item]);
  }
  return groups;
}

function sum<T>(items: T[], value: (item: T) => number): number {
  return items.reduce((total, item) => total + value(item), 0);
}
